import { useState } from "react";
import { motion, useViewportScroll, useTransform } from "framer-motion";
import { FaBook, FaUsers, FaMicroscope, FaLaptop, FaCity, FaTools } from "react-icons/fa";

const categories = [
  "All",
  "Academic",
  "Research",
  "Infrastructure",
  "ICT",
  "Training",
  "Maintenance",
  "Zonal",
];

const tetFundProjects = [
  { name: "Academic Manuscript Development", category: "Academic", icon: <FaBook /> },
  { name: "Academic Staff Training & Development", category: "Training", icon: <FaUsers /> },
  { name: "Academic Research Journal", category: "Research", icon: <FaMicroscope /> },
  { name: "Conference Attendance", category: "Research", icon: <FaMicroscope /> },
  { name: "Disaster Recovery", category: "Infrastructure", icon: <FaCity /> },
  { name: "Entrepreneurship", category: "Training", icon: <FaTools /> },
  { name: "Equipment Fabrication", category: "Infrastructure", icon: <FaTools /> },
  { name: "High Impact", category: "Research", icon: <FaMicroscope /> },
  { name: "ICT Support", category: "ICT", icon: <FaLaptop /> },
  { name: "Interventions", category: "Infrastructure", icon: <FaCity /> },
  { name: "Institution-Based Research", category: "Research", icon: <FaMicroscope /> },
  { name: "Library Development", category: "Academic", icon: <FaBook /> },
  { name: "National Research Fund", category: "Research", icon: <FaMicroscope /> },
  { name: "Physical Infrastructure/Program Upgrade", category: "Infrastructure", icon: <FaCity /> },
  { name: "Teaching Practice", category: "Academic", icon: <FaUsers /> },
  { name: "TETFund Project Maintenance", category: "Maintenance", icon: <FaTools /> },
  { name: "Zonal", category: "Zonal", icon: <FaCity /> },
];

const TetFundProjects = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedProject, setSelectedProject] = useState(null);
  const { scrollY } = useViewportScroll();

  // Parallax for background circles
  const blueCircleY = useTransform(scrollY, [0, 800], [0, 50]);
  const pinkCircleY = useTransform(scrollY, [0, 800], [0, -50]);

  // Scroll-based gradient color shift
  const gradientShift = useTransform(
    scrollY,
    [0, 1000],
    ["0deg, #bfdbfe 0%, #fbcfe8 100%", "180deg, #d8b4fe 0%, #fef3c7 100%"]
  );

  const filteredProjects =
    selectedCategory === "All"
      ? tetFundProjects
      : tetFundProjects.filter((p) => p.category === selectedCategory);

  return (
    <motion.section
      className="relative py-16 overflow-hidden min-h-screen"
      style={{
        background: gradientShift,
        backgroundImage: "linear-gradient(var(--scroll-gradient))",
      }}
    >
      {/* Parallax background circles */}
      <motion.div
        className="absolute -top-20 -left-20 w-72 h-72 bg-blue-200 rounded-full opacity-20 blur-3xl"
        style={{ y: blueCircleY }}
      />
      <motion.div
        className="absolute -bottom-20 -right-20 w-72 h-72 bg-pink-200 rounded-full opacity-20 blur-3xl"
        style={{ y: pinkCircleY }}
      />

      {/* Heading */}
      <motion.h2
        className="text-4xl md:text-5xl font-extrabold text-gray-900 text-center mb-6"
        initial={{ opacity: 0, y: -50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        TETFund Sponsored Projects
      </motion.h2>
      <motion.p
        className="text-center text-gray-700 mb-10 max-w-3xl mx-auto"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
      >
        Explore the diverse TETFund-sponsored projects at The Federal Polytechnic
        Bida designed to boost academic excellence, research, infrastructure, and
        staff development.
      </motion.p>

      {/* Category filters */}
      <div className="flex justify-center flex-wrap gap-3 mb-12">
        {categories.map((cat, idx) => (
          <button
            key={idx}
            onClick={() => setSelectedCategory(cat)}
            className={`px-4 py-2 rounded-full font-medium transition-all duration-300 ${
              selectedCategory === cat
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-blue-100"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Projects grid */}
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 px-6"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
      >
        {filteredProjects.map((project, index) => (
          <motion.div
            key={index}
            className="relative overflow-hidden rounded-2xl shadow-lg cursor-pointer p-6 text-center transition-transform duration-300 transform hover:scale-105 group"
            variants={{
              hidden: { opacity: 0, y: 40 },
              visible: { opacity: 1, y: 0 },
            }}
            whileHover={{ y: -5 }}
            onClick={() => setSelectedProject(project)}
          >
            {/* Gradient hover background */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-200 via-pink-200 to-purple-200 opacity-0 group-hover:opacity-50 transition-opacity duration-500 rounded-2xl blur-2xl pointer-events-none"></div>

            {/* Decorative circles */}
            <div className="absolute -top-10 -left-10 w-16 h-16 bg-blue-100 rounded-full opacity-20 blur-2xl"></div>
            <div className="absolute -bottom-10 -right-10 w-16 h-16 bg-pink-100 rounded-full opacity-20 blur-2xl"></div>

            <div className="text-4xl text-blue-500 mb-3 relative z-10">{project.icon}</div>
            <h3 className="text-lg font-semibold text-gray-800 relative z-10">{project.name}</h3>
            <p className="text-sm text-gray-500 relative z-10 mt-2">{project.category}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Project modal */}
      {selectedProject && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedProject(null)}
        >
          <motion.div
            className="bg-white rounded-3xl shadow-xl p-8 max-w-lg mx-4 relative"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-2xl font-bold"
              onClick={() => setSelectedProject(null)}
            >
              ×
            </button>
            <div className="text-5xl text-blue-500 mb-4">{selectedProject.icon}</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {selectedProject.name}
            </h2>
            <p className="text-gray-700 mb-2">
              <strong>Category:</strong> {selectedProject.category}
            </p>
            <p className="text-gray-700">
              This project is sponsored by TETFund to support academic excellence,
              research initiatives, staff development, and infrastructure
              improvement at The Federal Polytechnic Bida. Click outside to close.
            </p>
          </motion.div>
        </motion.div>
      )}
    </motion.section>
  );
};

export default TetFundProjects;
